import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Coins, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

const BillingSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Invalidate relevant queries to refresh user data
    queryClient.invalidateQueries({ queryKey: ['user'] });
    queryClient.invalidateQueries({ queryKey: ['user-tier'] });
    queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    queryClient.invalidateQueries({ queryKey: ['usage-stats'] });
    
    // Track purchase conversion
    const trackPurchaseConversion = async () => {
      try {
        const { trackPurchase } = await import('../lib/metaPixel');
        
        // Get purchase details from URL params if available
        const amount = searchParams.get('amount');
        const tierName = searchParams.get('tier');
        const type = searchParams.get('type'); // 'subscription' or 'credits'
        
        if (amount) {
          trackPurchase(
            parseFloat(amount), 
            'USD', 
            type === 'credits' ? 'credits' : 'subscription'
          );
        } else {
          // Default tracking if no amount specified
          trackPurchase(0, 'USD', 'subscription');
        }
      } catch (error) {
        console.error('Failed to track purchase conversion:', error);
      }
    };
    
    trackPurchaseConversion();
  }, [queryClient, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-10 h-10 text-green-600" />
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-2xl font-bold text-black mb-2">
            Payment Successful!
          </h1>
          <p className="text-black mb-6">
            Thank you for your purchase. Your account has been updated and you can start using your new features right away.
          </p>
        </motion.div>

        {/* Features Unlocked */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6"
        >
          <div className="flex items-center justify-center space-x-2 mb-3">
            <Crown className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-black">What's New</span>
          </div>
          
          <div className="space-y-2 text-sm text-black">
            <div className="flex items-center justify-center space-x-2">
              <Coins className="w-4 h-4 text-green-600" />
              <span>Credits added to your account</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Premium features unlocked</span>
            </div>
          </div>
        </motion.div>

        {/* Current Credits */}
        {user?.credits !== undefined && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gray-50 rounded-lg p-4 mb-6"
          >
            <p className="text-sm text-black mb-1">Your Current Balance</p>
            <div className="flex items-center justify-center space-x-2">
              <Coins className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-black">
                {user.credits.toFixed(0)} Credits
              </span>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-3"
        >
          <Button
            onClick={() => navigate('/app/dashboard')}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-xl font-medium transition-all duration-200 shadow-lg"
          >
            <span>Go to Dashboard</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <button
            onClick={() => navigate('/app/billing')}
            className="w-full py-3 rounded-xl font-medium text-black bg-white border-2 border-gray-300 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-colors"
          >
            View Billing Details
          </button>
        </motion.div>

        {/* Session Info (for debugging) */}
        {sessionId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 pt-4 border-t border-gray-200"
          >
            <p className="text-xs text-gray-500">
              Session ID: {sessionId.substring(0, 20)}...
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default BillingSuccessPage;